import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Stepper,
  Step,
  StepLabel,
  Button,
  CircularProgress,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from '@mui/material';
import AuthContext from '../contexts/AuthContext';
import { usePractice } from '../contexts/PracticeContext';
import StepIconOutlined from '../components/StepIconOutlined';

export default function NewOrder() {
  const apiBase = process.env.REACT_APP_PORTAL_API_URL || '';
  const navigate = useNavigate();
  const { tokens, authData } = useContext(AuthContext);
  const { selectedPractice } = usePractice();
  const professionalRecordId = authData?.professional_record?.id || '';
  const isProviderUser = Boolean(professionalRecordId);

  const steps = ['Select Patient', 'Select Product', 'Select Collection Site', 'Review Order'];
  const [activeStep, setActiveStep] = useState(0);

  // Step 1 state
  const [patientMode, setPatientMode] = useState('existing');
  // Search existing patient records
  const [searchText, setSearchText] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loadingSearch, setLoadingSearch] = useState(false);
  // Selected patient record ID
  const [selectedPatientId, setSelectedPatientId] = useState('');
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [newFirstName, setNewFirstName] = useState('');
  const [newLastName, setNewLastName] = useState('');
  const [newDob, setNewDob] = useState('');
  // Validation helper for YYYY-MM-DD
  const isValidDob = (str) => /^\d{4}-\d{2}-\d{2}$/.test(str);
  const [newMrn, setNewMrn] = useState('');

  // Step 2 state
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState('');
  // Search text for products
  const [productSearchText, setProductSearchText] = useState('');
  // State for collection site selection step
  const [collectionSites, setCollectionSites] = useState([]);
  const [loadingCollectionSites, setLoadingCollectionSites] = useState(false);
  const [collectionSearchText, setCollectionSearchText] = useState('');
  const [selectedCollectionSiteId, setSelectedCollectionSiteId] = useState('');

  // Provider selection (office staff create on behalf of providers)
  const [providerOptions, setProviderOptions] = useState([]);
  const [loadingProviders, setLoadingProviders] = useState(false);
  const [selectedProviderId, setSelectedProviderId] = useState(professionalRecordId);

  const [error, setError] = useState(null);

  useEffect(() => {
    if (professionalRecordId) {
      setSelectedProviderId(professionalRecordId);
    }
  }, [professionalRecordId]);

  useEffect(() => {
    if (patientMode === 'new') {
      setSelectedPatientId('');
      setSelectedPatient(null);
    }
  }, [patientMode]);

  // Fetch patients matching searchText for selected practice
  useEffect(() => {
    if (patientMode !== 'existing' || !tokens?.id_token || !selectedPractice?.id) return;
    setLoadingSearch(true);
    const params = new URLSearchParams({ organization_id: selectedPractice.id, search_text: searchText, limit: '10' });
    fetch(`${apiBase}/patients?${params.toString()}`, {
      headers: { Authorization: `Bearer ${tokens.id_token}` }
    })
      .then(res => {
        if (!res.ok) throw new Error('Failed to fetch patients');
        return res.json();
      })
      .then(data => setSearchResults(data.items || []))
      .catch(err => setError(err.message))
      .finally(() => setLoadingSearch(false));
  }, [searchText, tokens, selectedPractice, patientMode]);

  // Fetch products matching search text
  useEffect(() => {
    if (!tokens?.id_token) return;
    setLoadingProducts(true);
    const params = new URLSearchParams();
    if (productSearchText) params.append('search_text', productSearchText);
    params.append('limit', '10');
    fetch(`${apiBase}/products?${params.toString()}`, {
      headers: { Authorization: `Bearer ${tokens.id_token}` }
    })
      .then(res => {
        if (!res.ok) throw new Error('Failed to fetch products');
        return res.json();
      })
      .then(data => setProducts(data.items || []))
      .catch(err => setError(err.message))
      .finally(() => setLoadingProducts(false));
  }, [tokens, productSearchText]);
  // Fetch collection sites matching search text
  useEffect(() => {
    if (!tokens?.id_token) return;
    setLoadingCollectionSites(true);
    const params = new URLSearchParams();
    if (collectionSearchText) params.append('search_text', collectionSearchText);
    params.append('limit', '10');
    params.append('is_collection_site', 'true');
    fetch(`${apiBase}/organizations?${params.toString()}`, {
      headers: { Authorization: `Bearer ${tokens.id_token}` }
    })
      .then(res => {
        if (!res.ok) throw new Error('Failed to fetch collection sites');
        return res.json();
      })
      .then(data => setCollectionSites(data.items || []))
      .catch(err => setError(err.message))
      .finally(() => setLoadingCollectionSites(false));
  }, [tokens, collectionSearchText]);
  // Fetch provider options for office staff users
  useEffect(() => {
    if (isProviderUser || !tokens?.id_token || !selectedPractice?.id) {
      if (isProviderUser) {
        setProviderOptions([]);
      }
      return;
    }
    setLoadingProviders(true);
    const params = new URLSearchParams({ limit: '100', job: 'Provider' });
    fetch(`${apiBase}/organizations/${selectedPractice.id}/users?${params.toString()}`, {
      headers: { Authorization: `Bearer ${tokens.id_token}` }
    })
      .then(res => {
        if (!res.ok) throw new Error('Failed to fetch providers');
        return res.json();
      })
      .then(data => {
        const options = (data.items || [])
          .filter(item => item.professional_record_id)
          .map(item => ({
            id: item.professional_record_id,
            displayName: item.professional_first_name && item.professional_last_name
              ? `${item.professional_first_name} ${item.professional_last_name}`
              : (item.name || item.email || 'Provider'),
            email: item.email
          }));
        setProviderOptions(options);
        setSelectedProviderId(prev => {
          if (options.length === 1) {
            return options[0].id;
          }
          return options.some(opt => opt.id === prev) ? prev : '';
        });
      })
      .catch(err => setError(err.message))
      .finally(() => setLoadingProviders(false));
  }, [isProviderUser, tokens, selectedPractice, apiBase]);

  // Move to next step, checking duplicate MRN for new patients
  const handleNext = async () => {
    setError(null);
    if (activeStep === 0) {
      if (patientMode === 'existing') {
        if (!selectedPatientId) { setError('Please select a patient'); return; }
        if (!selectedPatient) {
          const match = searchResults.find(p => p.id === selectedPatientId);
          if (match) setSelectedPatient(match);
        }
      } else {
        // Validate fields
        if (!newFirstName || !newLastName || !isValidDob(newDob) || !newMrn) {
          setError('Complete all patient fields with valid data'); return;
        }
        // Check for existing MRN at this practice
        try {
          const params = new URLSearchParams({ organization_id: selectedPractice.id, mrn: newMrn, limit: '1' });
          const res = await fetch(`${apiBase}/patients?${params}`, {
            headers: { Authorization: `Bearer ${tokens.id_token}` }
          });
          if (!res.ok) throw new Error('Failed to validate MRN');
          const data = await res.json();
          if (data.items && data.items.length > 0 && data.items[0].mrn === newMrn) {
            setError('MRN already used at this practice'); return;
          }
        } catch (err) {
          console.error(err);
          setError(err.message || 'Error validating MRN'); return;
        }
      }
      setActiveStep(1);
    } else if (activeStep === 1) {
      if (!selectedProductId) { setError('Please select a product'); return; }
      setActiveStep(2);
    } else if (activeStep === 2) {
      if (!selectedCollectionSiteId) { setError('Please select a collection site'); return; }
      setActiveStep(3);
    }
  };
  // Confirm and submit patient (if new) and order
const handleConfirm = async () => {
  setError(null);
  const providerIdToUse = professionalRecordId || selectedProviderId;
  if (!providerIdToUse) {
    setError('Please select an ordering provider');
    return;
  }
  // Determine if selected product has a requisition form and open popup synchronously
  const chosenProduct = products.find(pr => pr.id === selectedProductId);
  let newWindow;
  if (chosenProduct?.requisition_form_id) {
    newWindow = window.open('', '_blank');
    }
    try {
      let patientId = selectedPatientId;
      if (patientMode === 'new') {
        // Create patient record
        const res1 = await fetch(`${apiBase}/patients`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tokens.id_token}` },
          body: JSON.stringify({
            first_name: newFirstName,
            last_name: newLastName,
            dob: newDob,
            mrn: newMrn,
            organization_id: selectedPractice.id
          })
        });
    if (!res1.ok) {
      const txt = await res1.text();
      throw new Error(txt || 'Failed to create patient record');
    }
        const dat = await res1.json();
        patientId = dat.id;
        setSelectedPatientId(dat.id);
        setSelectedPatient(dat);
      }
  // Create order
  const payload = {
    patient_record_id: patientId,
    provider_id: providerIdToUse,
    organization_id: selectedPractice.id,
    product_id: selectedProductId,
    collection_site_id: selectedCollectionSiteId
  };
      // Create order (organization_id in query is optional; backend reads from body)
      const res2 = await fetch(`${apiBase}/orders`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${tokens.id_token}`
        },
        body: JSON.stringify(payload)
      });
      if (!res2.ok) {
        const txt = await res2.text();
        throw new Error(txt || 'Failed to create order');
      }
      const created = await res2.json();
      // Populate the previously opened window with the requisition form
      if (newWindow) {
        try {
          const formRes = await fetch(`${apiBase}/orders/${created.id}/requisition_form`, {
            headers: { Authorization: `Bearer ${tokens.id_token}` }
          });
          if (!formRes.ok) throw new Error('Failed to download requisition form');
          const blob = await formRes.blob();
          const url = window.URL.createObjectURL(blob);
          newWindow.location.href = url;
        } catch (err) {
          console.error('Error opening requisition form:', err);
          newWindow.close();
        }
      }
      // Navigate to orders list with search pre-filled to the new order ID
      navigate(`/orders?search_text=${created.id}`);
    } catch (err) {
      console.error(err);
      if (newWindow) newWindow.close();
      setError(err.message);
    }
  };

  const handleBack = () => setActiveStep(prev => prev - 1);

  const isNextDisabled = () => {
    if (activeStep === 0) {
      return patientMode === 'existing'
        ? !selectedPatientId
      : !(newFirstName && newLastName && isValidDob(newDob) && newMrn);
    }
    if (activeStep === 1) {
      return !selectedProductId;
    }
    if (activeStep === 2) {
      return !selectedCollectionSiteId;
    }
    return false;
  };

  const selectedExistingPatient = patientMode === 'existing'
    ? (selectedPatient || searchResults.find(p => p.id === selectedPatientId) || null)
    : null;
  const reviewPatientName = patientMode === 'existing'
    ? [selectedExistingPatient?.first_name, selectedExistingPatient?.last_name].filter(Boolean).join(' ')
    : `${newFirstName} ${newLastName}`;
  const reviewPatientDob = patientMode === 'existing' ? selectedExistingPatient?.dob : newDob;
  const reviewPatientMrn = patientMode === 'existing' ? selectedExistingPatient?.mrn : newMrn;
  const selectedProduct = products.find(pr => pr.id === selectedProductId);
  const selectedCollectionSite = collectionSites.find(cs => cs.id === selectedCollectionSiteId);
  const orderingProviderName = authData?.professional_record
    ? [authData.professional_record.first_name, authData.professional_record.last_name].filter(Boolean).join(' ') ||
      authData.user?.name ||
      authData.user?.email
    : '';

  return (
    <Container disableGutters sx={{ mt: 2 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        New Order
      </Typography>
      <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
        {steps.map(label => (
          <Step key={label}>
            <StepLabel StepIconComponent={StepIconOutlined}>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>
      {error && (
        <Typography color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}
      <Box>
        {activeStep === 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <RadioGroup
              row
              value={patientMode}
              onChange={e => setPatientMode(e.target.value)}
            >
              <FormControlLabel
                value="existing"
                control={<Radio />}
                label="Existing Patient"
              />
              <FormControlLabel
                value="new"
                control={<Radio />}
                label="New Patient"
              />
            </RadioGroup>
            {patientMode === 'existing' ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  fullWidth
                  label="Search Patients"
                  value={searchText}
                  onChange={e => setSearchText(e.target.value)}
                />
                {loadingSearch ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : (
                  <TableContainer component={Paper} sx={{ maxHeight: 300 }}>
                    <Table stickyHeader size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>First Name</TableCell>
                          <TableCell>Last Name</TableCell>
                          <TableCell>DOB</TableCell>
                          <TableCell>MRN</TableCell>
                          <TableCell>Action</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {searchResults.map(p => (
                          <TableRow key={p.id}>
                            <TableCell>{p.first_name}</TableCell>
                            <TableCell>{p.last_name}</TableCell>
                            <TableCell>{p.dob}</TableCell>
                            <TableCell>{p.mrn}</TableCell>
                            <TableCell>
                              <Button
                                size="small"
                                variant={selectedPatientId === p.id ? 'contained' : 'outlined'}
                                onClick={() => { setSelectedPatientId(p.id); setSelectedPatient(p); }}
                              >
                                {selectedPatientId === p.id ? 'Selected' : 'Select'}
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>
            ) : (
              <>
                <TextField
                  fullWidth
                  label="First Name"
                  value={newFirstName}
                  onChange={e => setNewFirstName(e.target.value)}
                />
                <TextField
                  fullWidth
                  label="Last Name"
                  value={newLastName}
                  onChange={e => setNewLastName(e.target.value)}
                />
                <TextField
                  fullWidth
                  label="Date of Birth (YYYY-MM-DD)"
                  value={newDob}
                  onChange={e => setNewDob(e.target.value)}
                  error={!!newDob && !isValidDob(newDob)}
                  helperText={!!newDob && !isValidDob(newDob) ? 'Invalid format, expected YYYY-MM-DD' : ''}
                />
                <TextField
                  fullWidth
                  label="MRN"
                  value={newMrn}
                  onChange={e => setNewMrn(e.target.value)}
                />
              </>
            )}
          </Box>
        )}
        {activeStep === 1 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label="Search Products"
              value={productSearchText}
              onChange={e => setProductSearchText(e.target.value)}
            />
            {loadingProducts ? (
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              <TableContainer component={Paper} sx={{ maxHeight: 300 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell>Price</TableCell>
                      <TableCell>Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {products.map(pr => (
                      <TableRow key={pr.id}>
                        <TableCell>{pr.name}</TableCell>
                        <TableCell>{pr.description}</TableCell>
                        <TableCell>${(pr.price_in_cents / 100).toFixed(2)}</TableCell>
                        <TableCell>
                          <Button
                            size="small"
                            variant={selectedProductId === pr.id ? 'contained' : 'outlined'}
                            onClick={() => setSelectedProductId(pr.id)}
                          >
                            {selectedProductId === pr.id ? 'Selected' : 'Select'}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}
      </Box>
      {/* Step 3: Select Collection Site */}
      {activeStep === 2 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
          <TextField
            fullWidth
            label="Search Collection Sites"
            value={collectionSearchText}
            onChange={e => setCollectionSearchText(e.target.value)}
          />
          {loadingCollectionSites ? (
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <TableContainer component={Paper} sx={{ maxHeight: 300 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Street</TableCell>
                    <TableCell>City</TableCell>
                    <TableCell>State</TableCell>
                    <TableCell>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {collectionSites.map(cs => (
                    <TableRow key={cs.id}>
                      <TableCell>{cs.name}</TableCell>
                      <TableCell>{cs.street_1}</TableCell>
                      <TableCell>{cs.city}</TableCell>
                      <TableCell>{cs.state}</TableCell>
                      <TableCell>
                        <Button
                          size="small"
                          variant={selectedCollectionSiteId === cs.id ? 'contained' : 'outlined'}
                          onClick={() => setSelectedCollectionSiteId(cs.id)}
                        >
                          {selectedCollectionSiteId === cs.id ? 'Selected' : 'Select'}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}
      {/* Step 4: Review Order */}
      {activeStep === 3 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
          <Typography><strong>Practice:</strong> {selectedPractice?.name}</Typography>
          <Typography><strong>Patient:</strong> {reviewPatientName}</Typography>
          {reviewPatientDob && <Typography><strong>DOB:</strong> {reviewPatientDob}</Typography>}
          {reviewPatientMrn && <Typography><strong>MRN:</strong> {reviewPatientMrn}</Typography>}
          <Typography><strong>Collection Site:</strong> {selectedCollectionSite?.name}</Typography>
          <Typography><strong>Product:</strong> {selectedProduct?.name}</Typography>
          {isProviderUser ? (
            <Typography>
              <strong>Ordering Provider:</strong> {orderingProviderName}
            </Typography>
          ) : (
            <Box>
              <FormControl fullWidth disabled={loadingProviders}>
                <InputLabel id="provider-select-label">Ordering Provider</InputLabel>
                <Select
                  labelId="provider-select-label"
                  label="Ordering Provider"
                  value={selectedProviderId}
                  onChange={e => setSelectedProviderId(e.target.value)}
                >
                  <MenuItem value="" disabled>
                    Select a provider
                  </MenuItem>
                  {providerOptions.map(option => (
                    <MenuItem key={option.id} value={option.id}>
                      {option.displayName}{option.email ? ` (${option.email})` : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {!loadingProviders && providerOptions.length === 0 && (
                <Typography color="error" sx={{ mt: 1 }}>
                  No providers are assigned to this practice. Invite a provider before submitting an order.
                </Typography>
              )}
            </Box>
          )}
        </Box>
      )}
      {/* Navigation Buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
        <Button disabled={activeStep === 0} onClick={handleBack}>Back</Button>
        {activeStep < steps.length - 1 ? (
          <Button variant="contained" onClick={handleNext} disabled={isNextDisabled()}>Next</Button>
        ) : (
          <Button
            variant="contained"
            onClick={handleConfirm}
            disabled={!isProviderUser && (!selectedProviderId || loadingProviders)}
          >
            {selectedProduct?.requisition_form_id
              ? 'Confirm Order and Print Form'
              : 'Confirm Order'}
          </Button>
        )}
      </Box>
    </Container>
  );
}
